import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider, PaletteMode } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { modernTheme, darkTheme } from '../theme/modernTheme';
import i18n from '../i18n';
import { localizeTheme } from '../utils/locale';

interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as PaletteMode) || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // MUI's own texts and the text direction follow the UI language
  const [language, setLanguage] = useState(i18n.language);
  useEffect(() => {
    const onChange = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', onChange);
    setLanguage(i18n.language);
    return () => { i18n.off('languageChanged', onChange); };
  }, []);

  const toggleColorMode = () => {
    // Add a small delay to prevent flashing
    document.body.style.transition = 'background-color 0.3s ease-in-out';
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = React.useMemo(
    () => {
      const selectedTheme = mode === 'light' ? modernTheme : darkTheme;
      return localizeTheme(selectedTheme);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- localizeTheme() reads the language
    [mode, language]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};